import type { AreaRoutes } from "@/app/routes/hr/areaRoutes";
import { RequireCapability } from "@/app/session/RequireCapability";

/* Engagement and people reports: the company area for everyone (/company: announcements,
   policies, surveys, celebrations), HR's engagement tools (/people/engagement) and the people
   reports (/people/reports). Engagement pages answer 404 while the area is switched off; the
   hiring and payroll reports also without their area and its permission, as the server does. */
export const engagementRoutes: AreaRoutes = {
  people: [
    {
      element: <RequireCapability feature="engagement" />,
      children: [
        {
          path: "engagement",
          lazy: async () => ({
            Component: (await import("@/features/engagement/routes/EngagementHubLayout")).default,
          }),
          children: [
            {
              index: true,
              lazy: async () => ({
                Component: (await import("@/features/engagement/routes/ManageAnnouncementsPage"))
                  .default,
              }),
            },
            {
              path: "policies",
              lazy: async () => ({
                Component: (await import("@/features/engagement/routes/ManagePoliciesPage"))
                  .default,
              }),
            },
            {
              path: "surveys",
              lazy: async () => ({
                Component: (await import("@/features/engagement/routes/ManageSurveysPage")).default,
              }),
            },
            {
              path: "surveys/new",
              lazy: async () => ({
                Component: (await import("@/features/engagement/routes/SurveyBuilderPage")).default,
              }),
            },
            {
              path: "surveys/:surveyId/edit",
              lazy: async () => ({
                Component: (await import("@/features/engagement/routes/SurveyBuilderPage")).default,
              }),
            },
            {
              path: "surveys/:surveyId",
              lazy: async () => ({
                Component: (await import("@/features/engagement/routes/SurveyResultsPage")).default,
              }),
            },
          ],
        },
      ],
    },
    {
      path: "reports",
      lazy: async () => ({
        Component: (await import("@/features/reports/routes/ReportsLayout")).default,
      }),
      children: [
        {
          index: true,
          lazy: async () => ({
            Component: (await import("@/features/reports/routes/HeadcountReport")).default,
          }),
        },
        {
          path: "attendance",
          lazy: async () => ({
            Component: (await import("@/features/reports/routes/AttendanceReport")).default,
          }),
        },
        {
          path: "leave",
          lazy: async () => ({
            Component: (await import("@/features/reports/routes/LeaveReport")).default,
          }),
        },
        {
          path: "permissions",
          lazy: async () => ({
            Component: (await import("@/features/reports/routes/PermissionsReport")).default,
          }),
        },
        {
          path: "breaks",
          lazy: async () => ({
            Component: (await import("@/features/reports/routes/BreaksReport")).default,
          }),
        },
        {
          path: "leave-balances",
          lazy: async () => ({
            Component: (await import("@/features/reports/routes/LeaveBalancesReport")).default,
          }),
        },
        {
          path: "statutory-leave",
          lazy: async () => ({
            Component: (await import("@/features/reports/routes/StatutoryLeaveReport")).default,
          }),
        },
        {
          path: "utilization",
          lazy: async () => ({
            Component: (await import("@/features/reports/routes/UtilizationReport")).default,
          }),
        },
        {
          element: <RequireCapability feature="engagement" />,
          children: [
            {
              path: "enps",
              lazy: async () => ({
                Component: (await import("@/features/reports/routes/EnpsReport")).default,
              }),
            },
          ],
        },
        {
          element: <RequireCapability feature="recruitment" permission="recruitment.view" />,
          children: [
            {
              path: "hiring",
              lazy: async () => ({
                Component: (await import("@/features/reports/routes/HiringReport")).default,
              }),
            },
          ],
        },
        {
          element: <RequireCapability feature="payroll" permission="payroll.view" />,
          children: [
            {
              path: "payroll-cost",
              lazy: async () => ({
                Component: (await import("@/features/reports/routes/PayrollCostReport")).default,
              }),
            },
          ],
        },
      ],
    },
  ],
  employee: [],
  me: [],
  top: [
    {
      element: <RequireCapability feature="engagement" />,
      children: [
        {
          path: "company",
          lazy: async () => ({
            Component: (await import("@/features/engagement/routes/CompanyLayout")).default,
          }),
          children: [
            {
              index: true,
              lazy: async () => ({
                Component: (await import("@/features/engagement/routes/NewsPage")).default,
              }),
            },
            {
              path: "policies",
              lazy: async () => ({
                Component: (await import("@/features/engagement/routes/PoliciesPage")).default,
              }),
            },
            {
              path: "surveys",
              lazy: async () => ({
                Component: (await import("@/features/engagement/routes/SurveysPage")).default,
              }),
            },
            {
              path: "surveys/:surveyId",
              lazy: async () => ({
                Component: (await import("@/features/engagement/routes/SurveyAnswerPage")).default,
              }),
            },
            {
              path: "celebrations",
              lazy: async () => ({
                Component: (await import("@/features/engagement/routes/CelebrationsPage")).default,
              }),
            },
          ],
        },
      ],
    },
  ],
};
